import { useEffect, useRef } from "react";

const DEFAULT_SCROLL_THRESHOLD = 100;

export type UseInfiniteScrollOptions = {
  findParentScrollContainer?: boolean;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  scrollThreshold?: number;
};

/**
 * Hook for implementing infinite scroll functionality.
 * Triggers loadMore when user scrolls near the bottom of the container.
 *
 * @param options - Configuration options for infinite scroll
 * @param options.findParentScrollContainer - If true, finds the nearest parent with overflow-y-auto (default: false)
 * @param options.hasMore - Whether there are more items to load
 * @param options.isLoading - Whether initial data is loading
 * @param options.isLoadingMore - Whether more data is currently being loaded
 * @param options.loadMore - Callback function to load more items
 * @param options.scrollThreshold - Distance from bottom (in px) to trigger load (default: 100)
 * @returns Object containing scrollContainerRef to attach to the scrollable container
 */
export default function useInfiniteScroll({
  findParentScrollContainer = false,
  hasMore,
  isLoading,
  isLoadingMore,
  loadMore,
  scrollThreshold = DEFAULT_SCROLL_THRESHOLD,
}: UseInfiniteScrollOptions) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    let scrollContainer: HTMLElement | null = null;

    if (findParentScrollContainer) {
      const parent = element.closest(".overflow-y-auto");
      scrollContainer = parent instanceof HTMLElement ? parent : null;
    } else {
      scrollContainer = element;
    }

    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!scrollContainer) return;
      const { clientHeight, scrollHeight, scrollTop } = scrollContainer;

      if (
        scrollHeight - scrollTop - clientHeight < scrollThreshold &&
        hasMore &&
        !isLoadingMore &&
        !isLoading
      ) {
        loadMore();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, [
    findParentScrollContainer,
    hasMore,
    isLoadingMore,
    isLoading,
    loadMore,
    scrollThreshold,
  ]);

  return { scrollContainerRef };
}
